import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Avatar,
  Switch,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Tooltip,
  Badge,
  Button,
  message as antMessage
} from 'antd';
import {
  CrownOutlined,
  UserOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';

const { Title, Text, Paragraph } = Typography;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const { canModifyUser } = useUser();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(buildApiUrl('/api/users'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        const errorData = await response.json();
        antMessage.error(`Erro ao carregar usuários: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      antMessage.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (userId, field, value) => {
    setUpdatingUserId(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl(`/api/users/${userId}/permissions`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });

      if (response.ok) {
        antMessage.success('Permissões atualizadas com sucesso!');
        fetchUsers();
      } else {
        const errorData = await response.json();
        if (response.status === 403) {
          antMessage.error('Você não tem permissão para modificar este usuário ou o usuário é Master e está protegido.');
        } else {
          antMessage.error(errorData.message || 'Erro ao atualizar permissões');
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      antMessage.error('Erro ao atualizar permissões');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const getRoleTag = (role) => {
    const roleConfig = {
      master: {
        color: 'gold',
        icon: <SafetyOutlined />,
        text: 'Master'
      },
      admin: {
        color: '#3e79f7',
        icon: <CrownOutlined />,
        text: 'Administrador'
      },
      user: {
        color: 'default',
        icon: <UserOutlined />,
        text: 'Usuário'
      }
    };

    const config = roleConfig[role] || roleConfig.user;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Usuário',
      dataIndex: 'username',
      key: 'username',
      width: 250,
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (text, record) => (
        <Space size={12}>
          <Badge
            count={record.email_verified ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 0}
            offset={[-5, 35]}
          >
            <Avatar
              size={45}
              style={{
                backgroundColor: '#3e79f7',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              {text.charAt(0).toUpperCase()}
            </Avatar>
          </Badge>
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: '15px' }}>{text}</Text>
            <Space size={4}>
              <MailOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: '13px' }}>{record.email}</Text>
            </Space>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Função',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      align: 'center',
      filters: [
        { text: 'Master', value: 'master' },
        { text: 'Administrador', value: 'admin' },
        { text: 'Usuário', value: 'user' },
      ],
      onFilter: (value, record) => record.role === value,
      render: (role) => getRoleTag(role),
    },
    {
      title: 'Administrador',
      key: 'admin_permission',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Tooltip 
          title={
            !canModifyUser(record)
              ? 'Você não tem permissão para modificar este usuário'
              : record.role === 'admin' || record.role === 'master'
              ? 'Remover privilégios de administrador'
              : 'Conceder privilégios de administrador'
          }
        >
          <Switch
            checked={record.role === 'admin' || record.role === 'master'}
            onChange={(checked) => handlePermissionChange(record.id, 'role', checked ? 'admin' : 'user')}
            disabled={!canModifyUser(record)}
            loading={updatingUserId === record.id}
            checkedChildren={<CrownOutlined />}
            unCheckedChildren={<UserOutlined />}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      align: 'center',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (date) => (
        <Space size={4}>
          <CalendarOutlined style={{ color: '#8c8c8c' }} />
          <Text type="secondary">
            {new Date(date).toLocaleDateString('pt-BR')}
          </Text>
        </Space>
      ),
    },
  ];

  // Filtrar usuários baseado no texto de busca
  const filteredUsers = users.filter(user => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      user.username.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                Gerenciamento de Usuários
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Gerencie permissões e papéis dos usuários do sistema
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Área de Busca e Filtros */}
      <Card bordered={false}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <div style={{ position: 'relative' }}>
              <SearchOutlined 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#8c8c8c',
                  fontSize: '16px',
                  zIndex: 1
                }} 
              />
              <input
                type="text"
                className="filter-input"
                placeholder="Buscar por nome ou email..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  paddingLeft: '40px',
                  width: '100%',
                  height: '40px',
                  padding: '8px 12px 8px 40px',
                  border: '2px solid #d9d9d9',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1890ff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d9d9d9';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (document.activeElement !== e.target) {
                    e.target.style.borderColor = '#40a9ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (document.activeElement !== e.target) {
                    e.target.style.borderColor = '#d9d9d9';
                  }
                }}
              />
            </div>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
              loading={loading}
              size="large"
            >
              Atualizar
            </Button>
          </Col>
          <Col>
            <Space size={8}>
              <Text type="secondary">
                Total: <Text strong>{filteredUsers.length}</Text> usuário(s)
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabela de Usuários */}
      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={{
            spinning: loading,
            indicator: <Spin size="large" />,
            tip: 'Carregando usuários...',
          }}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} usuários`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onShowSizeChange: (current, size) => setPageSize(size)
          }}
          locale={{
            emptyText: (
              <Space direction="vertical" size={16} style={{ padding: '60px 0' }}>
                <UserOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  Nenhum usuário encontrado
                </Text>
              </Space>
            ),
            filterConfirm: 'Filtrar',
            filterReset: 'Limpar',
            filterEmptyText: 'Sem filtros',
            selectAll: 'Selecionar página atual',
            selectInvert: 'Inverter seleção',
            selectionAll: 'Selecionar todos',
            sortTitle: 'Ordenar',
            expand: 'Expandir linha',
            collapse: 'Colapsar linha',
            triggerDesc: 'Clique para ordenar decrescente',
            triggerAsc: 'Clique para ordenar crescente',
            cancelSort: 'Clique para cancelar ordenação',
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </Space>
  );
};

export default Users; 
